import { useState, useRef, useLayoutEffect, useEffect, useCallback, Dispatch, SetStateAction } from 'react';
import { PaginationParams } from '@/types/common';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';

type Item<T> = T & { id: string };
type Add<T> = (info: T) => void;
type Adds<T> = (info: Array<T>) => void;
type Update<T> = (info: T, updateByKey?: keyof T) => void;
type Updates<T> = (info: Array<T>) => void;
type Rm = (id: string) => void;
export type Fetch<T, Params extends Array<unknown> = Array<unknown>> = (
	...arg: Params
) => Promise<{ body?: Array<T>; pagination?: PaginationParams }>;

type UseListRes<T, Params extends Array<unknown> = Array<unknown>> = [
	Array<Item<T>>,
	number,
	boolean,
	{
		addItem: Add<Item<T>>;
		addItems: Adds<Item<T>>;
		updateItem: Update<Item<T>>;
		updateItems: Updates<Item<T>>;
		rmItem: Rm;
		rmItems: (ids: Array<string>) => void;
		fetchList: Fetch<T, Params>;
		serverError: boolean;
		clearData: () => void;
		setList: Dispatch<SetStateAction<Item<T>[]>>;
	},
];

const useDataList = <T extends { id: string }, Params extends Array<unknown> = Array<unknown>>({
	fetchApi,
}: {
	fetchApi: Fetch<T, Params>;
}): UseListRes<T, Params> => {
	const { t } = useTranslation();
	const [loading, setLoading] = useState<boolean>(false);
	const [list, setList] = useState<Array<Item<T>>>([]);
	const [total, setTotal] = useState<number>(0);
	const [serverError, setServerError] = useState<boolean>(false);

	const fetchApiRef = useRef(fetchApi);
	useLayoutEffect(() => {
		// 只采用已提交界面的 API，避免 Suspense 中未提交的渲染污染当前请求。
		fetchApiRef.current = fetchApi;
	}, [fetchApi]);

	const lastControllerRef = useRef<AbortController | null>(null);

	// 从 fetch 参数中提取 AbortController（支持直接作为参数传入，或放在 params / params.params 对象的 controller 属性中）
	const pickController = (args: Array<unknown>): AbortController | null => {
		const fromObj = (obj: unknown): AbortController | null => {
			if (obj && typeof obj === 'object') {
				const c = (obj as { controller?: unknown }).controller;
				if (c instanceof AbortController) return c;
			}
			return null;
		};
		for (const argItem of args) {
			if (argItem instanceof AbortController) return argItem;
			const direct = fromObj(argItem);
			if (direct) return direct;
			// 嵌套一层：fetchList({ params: { ..., controller } }) 这种结构
			const nested = fromObj((argItem as { params?: unknown } | null)?.params);
			if (nested) return nested;
		}
		return null;
	};

	// ✅ 监听键盘事件：按下 Esc 时中止当前进行中的请求
	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				if (lastControllerRef.current) {
					lastControllerRef.current.signal.onabort = () => notification.info({ message: t('common.reqCanceled'),description:t('common.escPressed') });
					lastControllerRef.current?.abort();
					// Esc 主动取消请求：结束 loading，返回空数据
					setLoading(false);
				}
			}
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, []);

	const fetch: Fetch<T, Params> = useCallback((...arg: Params) => {
		// ✅ 从 arg 里拿到 controller，用 ref 存储，供 Esc 中止请求使用
		lastControllerRef.current = pickController(arg);

		setLoading(true);
		setServerError(false);

		return new Promise((resolve, reject) => {
			fetchApiRef.current(...arg)
				.then((res) => {
					setLoading(false);
					setList(res.body ?? []);
					setTotal(res.pagination?.size ?? 0);
					resolve(res);
				})
				.catch((err) => {
					// 忽略取消的请求，不设置错误状态
					if (err?.name !== 'AbortError') {
						setLoading(false);
						setServerError(true);
						return reject(err);
					} else {
						return resolve({ body: [] });
					}
				})
			// .finally(() => {
			// 	setLoading(false);
			// });
		});
	}, []);

	const add: Add<Item<T>> = (item) => {
		setList((prev) => [item, ...prev]);
		setTotal(prev => prev + 1);
	};
	const adds: Adds<Item<T>> = (item) => {
		setList((prev) => [...item, ...prev]);
		setTotal(prev => prev + item.length);
	};

	const rm: Rm = (id) => {
		setList((prev) => prev.filter((item) => item.id !== id));
		setTotal(prev => prev - 1);
	};

	const rms = (ids: Array<string>) => {
		setList((prev) => prev.filter((item) => !ids.includes(item.id)));
		setTotal(prev => Math.max(0, prev - ids.length));
	};

	const update: Update<Item<T>> = (upItem, updateKey = 'id') => {
		setList((prev) => {
			return prev?.map((item) => (item[updateKey] === upItem[updateKey] ? upItem : item));
		});
	};

	const updates: Updates<Item<T>> = (upItems) => {
		let updateLists: Array<Item<T>> = upItems;
		setList((prev) => {
			const info: Array<Item<T>> = [];
			prev?.forEach((item) => {
				const newItem = upItems.find(up => up.id === item.id);
				updateLists = updateLists.filter(upI => upI.id !== item.id)
				newItem ? info.push(newItem) : info.push(item)
			})

			return [...info, ...updateLists];
		});

	};

	const clearData = useCallback(() => {
		setList([])
	}, []);

	return [
		list,
		total,
		loading,
		{ addItem: add, addItems: adds, updateItem: update, rmItem: rm, rmItems: rms, fetchList: fetch, serverError, updateItems: updates, clearData, setList },
	];
};

export default useDataList;
